package fr.manyverse.app.presentation.components.common_tabs

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.FloatingActionButtonDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.AlertType
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.model.TableMember
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.char
import kotlinx.datetime.toLocalDateTime
import fr.manyverse.app.presentation.components.clearFocusOnTap
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.alert_gm_report_confirm
import manyverse.shared.generated.resources.alert_gm_report_eject_hint
import manyverse.shared.generated.resources.alert_gm_report_manyverse_hint
import manyverse.shared.generated.resources.alert_gm_report_title
import manyverse.shared.generated.resources.alert_inappropriate
import manyverse.shared.generated.resources.alert_inappropriate_info
import manyverse.shared.generated.resources.alert_message_hint
import manyverse.shared.generated.resources.alert_sent_confirmation
import manyverse.shared.generated.resources.alert_uncomfortable
import manyverse.shared.generated.resources.alert_uncomfortable_info
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.event_author_game_master
import manyverse.shared.generated.resources.message_gm_info_banner
import manyverse.shared.generated.resources.message_gm_visibility_warning
import manyverse.shared.generated.resources.message_new_conversation
import manyverse.shared.generated.resources.message_recipients
import manyverse.shared.generated.resources.message_send_button_desc
import manyverse.shared.generated.resources.message_to_all
import manyverse.shared.generated.resources.messages_empty
import manyverse.shared.generated.resources.player_unknown
import manyverse.shared.generated.resources.security_alert
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock
import kotlin.time.Instant

// ---------------------------------------------------------------------------
// Domain helpers
// ---------------------------------------------------------------------------

private data class ConversationItem(
    val key: String,
    val otherParticipantIds: List<String>,
    val messages: List<GameEvent>   // newest first
)

private val ConversationItem.lastMessage get() = messages.firstOrNull()

internal fun conversationKey(participantIds: Collection<String>): String =
    participantIds.sorted().distinct().joinToString(",")

internal fun GameEvent.conversationKey(): String =
    conversationKey(targetParticipants + triggeredBy)

private fun buildConversations(
    messages: List<GameEvent>,
    currentUserId: String
): List<ConversationItem> =
    messages
        .groupBy { it.conversationKey() }
        .map { (key, msgs) ->
            ConversationItem(
                key = key,
                otherParticipantIds = key.split(",").filter { it != currentUserId },
                messages = msgs.sortedByDescending { it.createdAt }
            )
        }
        .sortedByDescending { it.lastMessage?.createdAt }

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

@Composable
fun MessagesTab(
    messages: List<GameEvent>,
    tableMembers: List<TableMember>,
    characters: List<fr.manyverse.app.domain.model.Character>,
    currentUserId: String,
    isGm: Boolean,
    onSendMessage: (text: String, recipientIds: List<String>) -> Unit,
    onSendAlert: (AlertType, String?) -> Unit = { _, _ -> },
    hasMoreOlderMessages: Boolean = false,
    isLoadingOlderMessages: Boolean = false,
    onLoadOlderMessages: () -> Unit = {},
    requestedConversationKey: String? = null,
    onConversationRequestHandled: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val gmLabel = stringResource(Res.string.event_author_game_master)
    val unknownPlayerLabel = stringResource(Res.string.player_unknown)

    fun nameFor(userId: String): String {
        val member = tableMembers.find { it.userId == userId }
        return when {
            member?.role == MemberRole.GAME_MASTER -> gmLabel
            member?.role == MemberRole.PLAYER -> characters.find { it.userId == userId }?.name
                ?: unknownPlayerLabel

            else -> unknownPlayerLabel
        }
    }

    fun isKnownParticipant(userId: String): Boolean {
        val member = tableMembers.find { it.userId == userId } ?: return false
        return member.role == MemberRole.GAME_MASTER || characters.any { it.userId == userId }
    }

    val conversations = remember(messages, currentUserId) {
        buildConversations(messages, currentUserId)
    }

    val eligibleRecipients = remember(tableMembers, currentUserId, isGm) {
        if (isGm) tableMembers.filter { it.role == MemberRole.PLAYER && it.characterId != null }
        else tableMembers.filter { it.userId != currentUserId && (it.role == MemberRole.GAME_MASTER || it.characterId != null) }
    }

    // null = conversation list | "new" = new conversation | else = conversation key
    var activeKey by rememberSaveable { mutableStateOf<String?>(null) }
    var showAlertDialog by remember { mutableStateOf(false) }
    var showGmAlertDialog by remember { mutableStateOf(false) }
    var alertSentVisible by remember { mutableStateOf(false) }

    // Ouverture demandée depuis l'extérieur (appui sur une notification de message)
    LaunchedEffect(requestedConversationKey) {
        requestedConversationKey ?: return@LaunchedEffect
        activeKey = requestedConversationKey
        onConversationRequestHandled()
    }

    LaunchedEffect(alertSentVisible) {
        if (alertSentVisible) {
            delay(3000)
            alertSentVisible = false
        }
    }

    Box(modifier = modifier.fillMaxSize()) {
        Column(Modifier.fillMaxSize()) {
            if (isGm) {
                MessagesBanner(
                    text = stringResource(Res.string.message_gm_info_banner),
                    modifier = Modifier.padding(end = 55.dp)
                )
            } else {
                MessagesBanner(
                    text = stringResource(Res.string.message_gm_visibility_warning),
                    modifier = Modifier.padding(end = 55.dp)
                )
            }

            when {
                activeKey == "new" -> NewConversationView(
                    eligibleRecipients = eligibleRecipients,
                    characters = characters,
                    gmLabel = gmLabel,
                    isGm = isGm,
                    onSend = { text, ids ->
                        onSendMessage(text, ids)
                        activeKey = conversationKey(ids + currentUserId)
                    },
                    onBack = { activeKey = null },
                    modifier = Modifier.weight(1f)
                )

                activeKey != null -> {
                    val key = activeKey!!
                    val recipientIds = key.split(",").filter { it != currentUserId }
                    ConversationThreadView(
                        title = recipientIds.joinToString(", ") { nameFor(it) },
                        messages = conversations.find { it.key == key }?.messages ?: emptyList(),
                        currentUserId = currentUserId,
                        recipientIds = recipientIds,
                        canSend = recipientIds.all { isKnownParticipant(it) },
                        onSendMessage = onSendMessage,
                        hasMoreOlderMessages = hasMoreOlderMessages,
                        isLoadingOlderMessages = isLoadingOlderMessages,
                        onLoadOlderMessages = onLoadOlderMessages,
                        onBack = { activeKey = null },
                        modifier = Modifier.weight(1f)
                    )
                }

                else -> ConversationListView(
                    conversations = conversations,
                    nameFor = { nameFor(it) },
                    hasEligibleRecipients = eligibleRecipients.isNotEmpty(),
                    onSelectConversation = { activeKey = it },
                    onNewConversation = { activeKey = "new" },
                    modifier = Modifier.weight(1f)
                )
            }
        }

        FloatingActionButton(
            onClick = { if (isGm) showGmAlertDialog = true else showAlertDialog = true },
            modifier = Modifier.align(Alignment.TopEnd).size(45.dp),
            containerColor = MaterialTheme.colorScheme.tertiaryContainer,
            contentColor = MaterialTheme.colorScheme.onTertiaryContainer,
            elevation = FloatingActionButtonDefaults.elevation()
        ) {
            Icon(
                imageVector = Icons.Default.Warning,
                contentDescription = stringResource(Res.string.security_alert)
            )
        }

        AnimatedVisibility(
            visible = alertSentVisible,
            enter = fadeIn(),
            exit = fadeOut(),
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(bottom = 80.dp, start = 16.dp, end = 16.dp)
        ) {
            Surface(
                shape = MaterialTheme.shapes.medium,
                color = MaterialTheme.colorScheme.tertiaryContainer,
                tonalElevation = 4.dp
            ) {
                Text(
                    text = stringResource(Res.string.alert_sent_confirmation),
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onTertiaryContainer
                )
            }
        }
    }

    if (showAlertDialog) {
        AlertConfirmDialog(
            onDismiss = { showAlertDialog = false },
            onConfirm = { alertType, message ->
                showAlertDialog = false
                onSendAlert(alertType, message)
                alertSentVisible = true
            }
        )
    }

    if (showGmAlertDialog) {
        AlertGmDialog(
            onDismiss = { showGmAlertDialog = false },
            onConfirm = { message ->
                showGmAlertDialog = false
                onSendAlert(AlertType.INAPPROPRIATE, message)
                alertSentVisible = true
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AlertConfirmDialog(
    onDismiss: () -> Unit,
    onConfirm: (AlertType, String?) -> Unit
) {
    val scope = rememberCoroutineScope()
    val uncomfortableTooltipState = rememberTooltipState()
    val inappropriateTooltipState = rememberTooltipState()
    var message by remember { mutableStateOf("") }
    val trimmedMessage = message.trim().ifBlank { null }

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.security_alert)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value = message,
                    onValueChange = { message = it },
                    label = { Text(stringResource(Res.string.alert_message_hint)) },
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                    minLines = 2,
                    modifier = Modifier.fillMaxWidth()
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    OutlinedButton(
                        onClick = { onConfirm(AlertType.UNCOMFORTABLE, trimmedMessage) },
                        modifier = Modifier.weight(1f)
                    ) {
                        Text(
                            text = stringResource(Res.string.alert_uncomfortable),
                            textAlign = TextAlign.Center
                        )
                    }
                    TooltipBox(
                        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(
                            TooltipAnchorPosition.Above
                        ),
                        tooltip = {
                            PlainTooltip {
                                Text(stringResource(Res.string.alert_uncomfortable_info))
                            }
                        },
                        state = uncomfortableTooltipState
                    ) {
                        IconButton(onClick = { scope.launch { uncomfortableTooltipState.show() } }) {
                            Icon(
                                imageVector = Icons.Default.Info,
                                contentDescription = stringResource(Res.string.alert_uncomfortable_info),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    OutlinedButton(
                        onClick = { onConfirm(AlertType.INAPPROPRIATE, trimmedMessage) },
                        modifier = Modifier.weight(1f)
                    ) {
                        Text(
                            text = stringResource(Res.string.alert_inappropriate),
                            textAlign = TextAlign.Center
                        )
                    }
                    TooltipBox(
                        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(
                            TooltipAnchorPosition.Above
                        ),
                        tooltip = {
                            PlainTooltip {
                                Text(stringResource(Res.string.alert_inappropriate_info))
                            }
                        },
                        state = inappropriateTooltipState
                    ) {
                        IconButton(onClick = { scope.launch { inappropriateTooltipState.show() } }) {
                            Icon(
                                imageVector = Icons.Default.Info,
                                contentDescription = stringResource(Res.string.alert_inappropriate_info),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.cancel))
            }
        }
    )
}

@Composable
private fun AlertGmDialog(
    onDismiss: () -> Unit,
    onConfirm: (String?) -> Unit
) {
    var message by remember { mutableStateOf("") }

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.alert_gm_report_title)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(
                    text = stringResource(Res.string.alert_gm_report_eject_hint),
                    style = MaterialTheme.typography.bodyMedium
                )
                Text(
                    text = stringResource(Res.string.alert_gm_report_manyverse_hint),
                    style = MaterialTheme.typography.bodyMedium
                )
                OutlinedTextField(
                    value = message,
                    onValueChange = { message = it },
                    label = { Text(stringResource(Res.string.alert_message_hint)) },
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                    minLines = 2,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(onClick = onDismiss) {
                    Text(stringResource(Res.string.cancel))
                }
                TextButton(onClick = { onConfirm(message.trim().ifBlank { null }) }) {
                    Text(stringResource(Res.string.alert_gm_report_confirm))
                }
            }
        }
    )
}

// ---------------------------------------------------------------------------
// Conversation list
// ---------------------------------------------------------------------------

@Composable
private fun ConversationListView(
    conversations: List<ConversationItem>,
    nameFor: (String) -> String,
    hasEligibleRecipients: Boolean,
    onSelectConversation: (String) -> Unit,
    onNewConversation: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(modifier = modifier.fillMaxSize()) {
        if (conversations.isEmpty()) {
            Text(
                text = stringResource(Res.string.messages_empty),
                style = MaterialTheme.typography.bodyLarge,
                textAlign = TextAlign.Center,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.align(Alignment.Center)
            )
        } else {
            LazyColumn(modifier = Modifier.fillMaxSize()) {
                items(conversations, key = { it.key }) { conv ->
                    ConversationRow(
                        conversation = conv,
                        nameFor = nameFor,
                        onClick = { onSelectConversation(conv.key) }
                    )
                    HorizontalDivider()
                }
            }
        }

        if (hasEligibleRecipients) {
            FloatingActionButton(
                onClick = onNewConversation,
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(vertical = 16.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Edit,
                    contentDescription = stringResource(Res.string.message_new_conversation)
                )
            }
        }
    }
}

@Composable
private fun ConversationRow(
    conversation: ConversationItem,
    nameFor: (String) -> String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val last = conversation.lastMessage
    val content = last?.content as? EventContent.MessageSent
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(2.dp)
        ) {
            Text(
                text = conversation.otherParticipantIds.joinToString(", ") { nameFor(it) },
                fontWeight = FontWeight.Bold,
                style = MaterialTheme.typography.bodyLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            if (content != null) {
                Text(
                    text = "${content.senderName} : ${content.text}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
        if (last != null) {
            Text(
                text = formatTimestamp(last.createdAt),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

// ---------------------------------------------------------------------------
// Conversation thread
// ---------------------------------------------------------------------------

@Composable
private fun ConversationThreadView(
    title: String,
    messages: List<GameEvent>,
    currentUserId: String,
    recipientIds: List<String>,
    canSend: Boolean,
    onSendMessage: (text: String, recipientIds: List<String>) -> Unit,
    onBack: () -> Unit,
    hasMoreOlderMessages: Boolean = false,
    isLoadingOlderMessages: Boolean = false,
    onLoadOlderMessages: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    var textValue by rememberSaveable(stateSaver = TextFieldValue.Saver) {
        mutableStateOf(TextFieldValue(""))
    }
    val listState = rememberLazyListState()

    LaunchedEffect(messages.size) {
        if (messages.isNotEmpty()) listState.animateScrollToItem(0)
    }

    // Pagination paresseuse : déclenche un fetch quand l'utilisateur s'approche
    // du haut visuel (= dernier item dans la liste reverseLayout).
    val shouldLoadOlder by remember(listState, hasMoreOlderMessages, isLoadingOlderMessages) {
        derivedStateOf {
            if (!hasMoreOlderMessages || isLoadingOlderMessages) return@derivedStateOf false
            val info = listState.layoutInfo
            val total = info.totalItemsCount
            if (total == 0) return@derivedStateOf false
            val lastVisibleIndex = info.visibleItemsInfo.lastOrNull()?.index ?: return@derivedStateOf false
            lastVisibleIndex >= total - 5
        }
    }
    LaunchedEffect(shouldLoadOlder) {
        if (shouldLoadOlder) onLoadOlderMessages()
    }

    Column(modifier = modifier.fillMaxSize()) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth()
        ) {
            IconButton(onClick = onBack) {
                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = stringResource(Res.string.back))
            }
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f)
            )
        }
        HorizontalDivider()

        if (messages.isEmpty()) {
            Box(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = stringResource(Res.string.messages_empty),
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        } else {
            LazyColumn(
                state = listState,
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(6.dp),
                contentPadding = PaddingValues(vertical = 8.dp),
                reverseLayout = true
            ) {
                items(messages, key = { it.id }) { event ->
                    val content = event.content as? EventContent.MessageSent ?: return@items
                    MessageBubble(
                        senderName = content.senderName,
                        text = content.text,
                        timestamp = event.createdAt,
                        isMine = event.triggeredBy == currentUserId,
                        showSenderName = recipientIds.size > 1
                    )
                }
                if (isLoadingOlderMessages) {
                    item(key = "loading_older") {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(8.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(20.dp),
                                strokeWidth = 2.dp
                            )
                        }
                    }
                }
            }
        }

        if (canSend) {
            val sendMessage = {
                if (textValue.text.isNotBlank()) {
                    onSendMessage(textValue.text, recipientIds)
                    textValue = TextFieldValue("")
                }
            }
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 4.dp, bottom = 8.dp)
            ) {
                OutlinedTextField(
                    value = textValue,
                    onValueChange = { textValue = it },
                    modifier = Modifier
                        .weight(1f)
                        .onPreviewKeyEvent { keyEvent ->
                            if (keyEvent.key == Key.Enter && keyEvent.type == KeyEventType.KeyDown) {
                                if (keyEvent.isShiftPressed) {
                                    textValue = textValue.insertNewline()
                                    true
                                } else {
                                    sendMessage()
                                    true
                                }
                            } else {
                                false
                            }
                        },
                    maxLines = 3,
                    placeholder = { Text("…") },
                    keyboardOptions = KeyboardOptions(
                        capitalization = KeyboardCapitalization.Sentences,
                        imeAction = ImeAction.Send
                    ),
                    keyboardActions = KeyboardActions(onSend = { sendMessage() })
                )
                Spacer(Modifier.width(8.dp))
                IconButton(
                    onClick = sendMessage,
                    enabled = textValue.text.isNotBlank()
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.Send,
                        contentDescription = stringResource(Res.string.message_send_button_desc)
                    )
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// New conversation
// ---------------------------------------------------------------------------

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun NewConversationView(
    eligibleRecipients: List<TableMember>,
    characters: List<fr.manyverse.app.domain.model.Character>,
    gmLabel: String,
    isGm: Boolean,
    onSend: (text: String, recipientIds: List<String>) -> Unit,
    onBack: () -> Unit,
    modifier: Modifier = Modifier
) {
    val allPlayerIds = remember(eligibleRecipients, isGm) {
        eligibleRecipients.filter { isGm || it.role == MemberRole.PLAYER }.map { it.userId }
    }

    var selectedIds by remember { mutableStateOf(emptySet<String>()) }
    var textValue by rememberSaveable(stateSaver = TextFieldValue.Saver) {
        mutableStateOf(TextFieldValue(""))
    }

    val isAllSelected = selectedIds.containsAll(allPlayerIds) && allPlayerIds.isNotEmpty()
    val canSend = textValue.text.isNotBlank() && selectedIds.isNotEmpty()

    Column(modifier = modifier.fillMaxSize()) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth()
        ) {
            IconButton(onClick = onBack) {
                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = stringResource(Res.string.back))
            }
            Text(
                text = stringResource(Res.string.message_new_conversation),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
        }
        HorizontalDivider()

        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp)
        ) {
            Text(
                text = stringResource(Res.string.message_recipients),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(end = 6.dp)
            )
            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                FilterChip(
                    selected = isAllSelected,
                    onClick = {
                        selectedIds = if (isAllSelected) emptySet() else allPlayerIds.toSet()
                    },
                    label = { Text(stringResource(Res.string.message_to_all)) }
                )
                eligibleRecipients.forEach { member ->
                    val label = when (member.role) {
                        MemberRole.GAME_MASTER -> gmLabel
                        else -> characters.find { it.userId == member.userId }?.name
                            ?: member.userId
                    }
                    FilterChip(
                        selected = member.userId in selectedIds,
                        onClick = {
                            selectedIds = if (member.userId in selectedIds)
                                selectedIds - member.userId
                            else
                                selectedIds + member.userId
                        },
                        label = { Text(label) }
                    )
                }
            }
        }

        HorizontalDivider()

        Spacer(modifier = Modifier.weight(1f))

        val sendMessage = {
            if (canSend) onSend(textValue.text, selectedIds.toList())
        }
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 4.dp, bottom = 8.dp)
        ) {
            OutlinedTextField(
                value = textValue,
                onValueChange = { textValue = it },
                enabled = selectedIds.isNotEmpty(),
                modifier = Modifier
                    .weight(1f)
                    .onPreviewKeyEvent { keyEvent ->
                        if (keyEvent.key == Key.Enter && keyEvent.type == KeyEventType.KeyDown) {
                            if (keyEvent.isShiftPressed) {
                                textValue = textValue.insertNewline()
                                true
                            } else {
                                sendMessage()
                                true
                            }
                        } else {
                            false
                        }
                    },
                maxLines = 3,
                placeholder = { Text("…") },
                keyboardOptions = KeyboardOptions(
                    capitalization = KeyboardCapitalization.Sentences,
                    imeAction = ImeAction.Send
                ),
                keyboardActions = KeyboardActions(onSend = { sendMessage() })
            )
            Spacer(Modifier.width(8.dp))
            IconButton(
                onClick = sendMessage,
                enabled = canSend
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.Send,
                    contentDescription = stringResource(Res.string.message_send_button_desc)
                )
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

@Composable
private fun MessageBubble(
    senderName: String,
    text: String,
    timestamp: Instant,
    isMine: Boolean,
    showSenderName: Boolean = true,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp),
        horizontalAlignment = if (isMine) Alignment.End else Alignment.Start
    ) {
        if (!isMine && showSenderName) {
            Text(
                text = senderName,
                fontWeight = FontWeight.Bold,
                fontSize = 12.sp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(horizontal = 4.dp)
            )
        }
        Surface(
            shape = MaterialTheme.shapes.medium,
            color = if (isMine) MaterialTheme.colorScheme.primaryContainer
            else MaterialTheme.colorScheme.secondaryContainer
        ) {
            Text(
                text = text,
                modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                style = MaterialTheme.typography.bodyMedium,
                color = if (isMine) MaterialTheme.colorScheme.onPrimaryContainer
                else MaterialTheme.colorScheme.onSecondaryContainer
            )
        }
        Text(
            text = formatTimestamp(timestamp),
            fontSize = 10.sp,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(horizontal = 4.dp, vertical = 2.dp)
        )
    }
}

// ---------------------------------------------------------------------------
// Date/time formatting
// ---------------------------------------------------------------------------

private fun TextFieldValue.insertNewline(): TextFieldValue {
    val start = selection.min
    val end = selection.max
    val newText = text.substring(0, start) + "\n" + text.substring(end)
    return copy(text = newText, selection = TextRange(start + 1))
}

private fun formatTimestamp(instant: Instant): String {
    val tz = TimeZone.currentSystemDefault()
    val dt = instant.toLocalDateTime(tz)
    val today = Clock.System.now().toLocalDateTime(tz).date
    val timeFmt = LocalDateTime.Format { hour(); char(':'); minute() }
    return if (dt.date == today) {
        dt.format(timeFmt)
    } else {
        dt.format(LocalDateTime.Format {
            day(); char('/'); monthNumber(); char(' '); hour(); char(
            ':'
        ); minute()
        })
    }
}
